import React, { useEffect, useMemo, useState } from "react";
import { Refresh } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import WorkflowCanvas from "./WorkflowCanvas";

const metricMap = (items, key) =>
  Object.fromEntries((items || []).map((item) => [item[key], item]));

export default function PublishedWorkflowOverview({
  workflow,
  version: selectedVersion,
  criteria,
  metrics,
  loading,
  error,
  onRefresh,
  narrow,
}) {
  const version = selectedVersion || workflow.currentPublishedVersion;
  const [mode, setMode] = useState(narrow ? "list" : "visual");
  const [selectedKey, setSelectedKey] = useState(version?.stages?.[0]?.key);
  useEffect(() => {
    if (narrow) setMode("list");
  }, [narrow]);
  useEffect(() => {
    setSelectedKey(version?.stages?.[0]?.key);
  }, [version?.id]);
  const stagesById = useMemo(
    () => metricMap(metrics?.stages, "stageId"),
    [metrics],
  );
  const selectedStage = version?.stages?.find(
    (stage) => stage.key === selectedKey,
  );
  const selectedMetrics = stagesById[selectedStage?.id];
  const templatesById = metricMap(selectedMetrics?.templates, "templateId");
  const selectOnly = (action) => {
    if (action.type === "select") setSelectedKey(action.key);
  };
  if (!version) return null;
  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h5">Published workflow</Typography>
          <Typography variant="body2" color="text.secondary">
            Published version {version.versionNumber}. Existing enrollments
            remain attached to the version on which they started.
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={onRefresh} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {metrics?.olderVersionActive > 0 && (
        <Alert severity="info">
          {metrics.olderVersionActive} active user
          {metrics.olderVersionActive === 1 ? " is" : "s are"} continuing on an
          earlier published version.
        </Alert>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip label={`${metrics?.active || 0} active`} color="primary" />
        <Chip label={`${metrics?.completed || 0} completed`} />
        <Chip label={`${metrics?.stopped || 0} stopped`} />
        <Chip label={`${metrics?.ineligible || 0} ineligible`} />
        {loading && <CircularProgress size={24} />}
      </Stack>
      <Tabs
        value={mode}
        onChange={(_, value) => setMode(value)}
        aria-label="Live workflow view"
      >
        <Tab value="visual" label="Visual flow" disabled={narrow} />
        <Tab value="list" label="Accessible list" />
      </Tabs>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 7 }}>
          {mode === "visual" ? (
            <WorkflowCanvas
              stages={version.stages}
              transitions={version.transitions}
              criteria={criteria}
              selectedKey={selectedKey}
              dispatch={selectOnly}
              readOnly
              stageMetrics={stagesById}
            />
          ) : (
            <Paper aria-label="Published workflow stage list">
              <List>
                {version.stages.map((stage, index) => (
                  <ListItemButton
                    key={stage.id}
                    selected={stage.key === selectedKey}
                    onClick={() => setSelectedKey(stage.key)}
                  >
                    <ListItemText
                      primary={`${index + 1}. ${stage.name}`}
                      secondary={`${stagesById[stage.id]?.active || 0} active users · ${stage.templates.length} emails`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">{selectedStage?.name}</Typography>
            <Typography color="text.secondary" variant="body2" mb={2}>
              {selectedMetrics?.active || 0} active users at this stage
            </Typography>
            {selectedStage?.advanceAfterEmailSequence && (
              <Chip
                size="small"
                sx={{ mb: 2 }}
                label="Advances after final email"
              />
            )}
            <Stack spacing={1.5}>
              {(selectedStage?.templates || []).map((template, index) => {
                const item = templatesById[template.id] || {};
                return (
                  <Box key={template.id}>
                    <Typography fontWeight={700}>
                      {index + 1}. {template.name || template.subject}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Chip
                        size="small"
                        color="primary"
                        label={`${item.waiting || 0} waiting`}
                      />
                      <Chip size="small" label={`${item.queued || 0} queued`} />
                      <Chip
                        size="small"
                        label={`${item.sending || 0} sending`}
                      />
                      <Chip size="small" label={`${item.sent || 0} sent`} />
                      <Chip
                        size="small"
                        color={item.failed ? "error" : "default"}
                        label={`${item.failed || 0} failed`}
                      />
                      <Chip
                        size="small"
                        label={`${item.skipped || 0} skipped`}
                      />
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
}
